import { FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { SmartListFrequency } from "@/enum/list.enum";

const CreateSmartListSchema = z.object({
	name: z.string().min(1, { message: "Required" }).trim(),
	frequency: z.nativeEnum(SmartListFrequency),
});

type CreateSmartListValues = z.infer<typeof CreateSmartListSchema>;

interface CreateSmartListModalProps {
	isOpen: boolean;
	isLoading: boolean;
	filterCount: number;
	semanticSearchQuery?: string;
	onClose: () => void;
	onSubmit: (values: CreateSmartListValues) => void;
}

export const CreateSmartListModal: FunctionComponent<CreateSmartListModalProps> = ({
	isOpen,
	isLoading,
	filterCount,
	semanticSearchQuery,
	onClose,
	onSubmit,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`contacts.smartList.${key}`), [t]);

	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<CreateSmartListValues>({
		resolver: zodResolver(CreateSmartListSchema),
		defaultValues: {
			name: "",
			frequency: SmartListFrequency.DAILY,
		},
	});

	const frequencyOptions: AutoCompleteItem[] = useMemo(
		() =>
			Object.values(SmartListFrequency).map((freq) => ({
				id: freq,
				title: ts(freq),
			})),
		[ts]
	);

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			size="sm"
			submitButtonText={ts("createSmartList")}
			title={ts("createSmartListTitle")}
		>
			<div className="space-y-4">
				<div className="rounded-lg bg-brand-white p-3 text-sm text-text-meta">
					{ts("infoBanner")}
				</div>

				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<InputField
							error={errors.name?.message}
							handleChange={(e) => field.onChange(e.target.value)}
							label={ts("listName")}
							name="name"
							placeholder={ts("listNamePlaceholder")}
							showError={!!errors.name}
							value={field.value}
						/>
					)}
				/>

				<div>
					<div className="text-sm font-medium text-gray-900 mb-2">{ts("filterQuery")}</div>
					<div className="rounded-lg border border-border-light p-3 text-sm text-text-meta">
						{filterCount > 0 && (
							<div>{t("contacts.smartList.activeFilters", { count: filterCount })}</div>
						)}
						{semanticSearchQuery && (
							<div>{t("contacts.smartList.semanticSearch", { query: semanticSearchQuery })}</div>
						)}
					</div>
				</div>

				<Controller
					control={control}
					name="frequency"
					render={({ field }) => (
						<Dropdown
							data={frequencyOptions}
							defaultValue={frequencyOptions.find((o) => o.id === field.value)}
							error={!!errors.frequency}
							floating
							handleSelect={(item) => field.onChange(item.id)}
							label={ts("updateFrequency")}
						/>
					)}
				/>
			</div>
		</Modal>
	);
};
